import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { siteInfo } from "@/mocks/site";

const navItems = [
  { label: "首页", to: "/", icon: "ri-home-5-line" },
  { label: "剧集", to: "/category", icon: "ri-play-list-line" },
  { label: "剧情介绍", to: "/plot", icon: "ri-book-open-line" },
  { label: "关于我们", to: "/about", icon: "ri-information-line" },
];

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-2 group">
            <span
              className={`flex items-center justify-center w-9 h-9 rounded-md transition-colors ${
                solid ? "bg-primary-500 text-background-50" : "bg-primary-500 text-background-50"
              }`}
            >
              <i className="ri-film-line text-lg"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-heading text-lg md:text-xl font-bold transition-colors ${
                  solid ? "text-foreground-950" : "text-background-50"
                }`}
              >
                {siteInfo.name}
              </span>
              <span
                className={`text-[10px] tracking-widest uppercase transition-colors ${
                  solid ? "text-foreground-500" : "text-background-200"
                }`}
              >
                {siteInfo.englishName}
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? solid
                        ? "text-primary-600 bg-primary-50"
                        : "text-background-50 bg-background-50/15"
                      : solid
                        ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                        : "text-background-100 hover:text-background-50 hover:bg-background-50/10"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/search"
              className={`flex items-center justify-center w-10 h-10 rounded-md ml-1 transition-colors ${
                solid
                  ? "text-foreground-700 hover:text-primary-600 hover:bg-background-100"
                  : "text-background-100 hover:text-background-50 hover:bg-background-50/10"
              }`}
              aria-label="搜索"
            >
              <i className="ri-search-line text-lg"></i>
            </Link>
          </nav>

          <button
            type="button"
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-md transition-colors cursor-pointer ${
              solid ? "text-foreground-950 hover:bg-background-100" : "text-background-50 hover:bg-background-50/10"
            }`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="菜单"
          >
            <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70 animate-fade-in">
          <nav className="flex flex-col px-4 py-3">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "text-primary-600 bg-primary-50"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                <i className={`${item.icon} text-lg`}></i>
                {item.label}
              </NavLink>
            ))}
            <NavLink
              to="/search"
              className="flex items-center gap-3 px-3 py-3 rounded-md text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-search-line text-lg"></i>
              搜索
            </NavLink>
          </nav>
        </div>
      )}
    </header>
  );
}